import { useState } from 'react'
import { Link } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { categoryUnitsApi } from '@/api/endpoints'
import { errorMessage } from '@/api/client'
import Drawer from '@/components/common/Drawer'
import { Alert, Empty, ErrorNote, Loading, PageHeader } from '@/components/common/Ui'
import { PERMS, can, useAuth } from '@/store/auth'

// Digits, and at most one decimal point with up to three places — the column
// is NUMERIC(12,3), and fine wire really does come in bundles under a kilo.
// Held as text rather than type="number" on purpose: a number input still
// accepts 'e', '+' and '-', and reports a value it rejected as an empty
// string, so a typo would read as "clear this weight".
const DIGITS = /^\d{0,6}(\.\d{0,3})?$/

/**
 * What one roll, bundle or bag weighs, per category.
 *
 * A roll is not a weight, so a line ordered in rolls cannot be priced until
 * somebody says what a roll of that category weighs. Every blank cell here is
 * a line that will land in the review queue instead of being priced, which is
 * why the blanks are shown rather than hidden.
 *
 * Edited a category at a time, in a drawer like the rest of the catalog
 * screens. Someone setting up a category knows its rolls and its bundles in
 * the same breath, so they belong in one form and one save.
 */
export default function UnitWeights() {
  const { user } = useAuth()
  const queryClient = useQueryClient()
  // { categoryId, categoryName, cells, values: { UNIT_CODE: '50' } }. Values
  // stay strings while being typed, so a half-entered '0.' is left alone.
  const [form, setForm] = useState(null)
  const [error, setError] = useState('')
  const [notice, setNotice] = useState('')

  const editable = can(user, PERMS.productsWrite)

  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ['category-units'],
    queryFn: categoryUnitsApi.list,
  })

  const save = useMutation({
    mutationFn: ({ categoryId, weights }) =>
      categoryUnitsApi.saveRow(categoryId, weights),
    onSuccess: (_rows, vars) => {
      queryClient.invalidateQueries({ queryKey: ['category-units'] })
      setForm(null)
      setError('')
      setNotice(`${vars.categoryName} saved.`)
    },
    onError: (e) => setError(errorMessage(e)),
  })

  if (isLoading) return <Loading />
  if (isError) {
    return <ErrorNote message="Could not load unit weights" onRetry={refetch} />
  }

  // Every category offers the same units, so one header row serves the table.
  const units = data[0]?.units ?? []
  const missing = data.reduce(
    (n, row) => n + row.units.filter((c) => c.source === 'none').length,
    0,
  )

  const startEditing = (row) => {
    setNotice('')
    setError('')
    setForm({
      categoryId: row.category_id,
      categoryName: row.category_name,
      cells: row.units,
      values: Object.fromEntries(
        row.units.map((c) => [
          c.unit_code,
          c.source === 'category' ? String(Number(c.kg_per_unit)) : '',
        ]),
      ),
    })
  }

  const commit = (event) => {
    event.preventDefault()
    const weights = []
    for (const [unit_code, raw] of Object.entries(form.values)) {
      const text = String(raw).trim()
      if (!text) {
        weights.push({ unit_code, kg_per_unit: null })
        continue
      }
      const kg = Number(text)
      if (!Number.isFinite(kg) || kg <= 0) {
        setError(`${unit_code}: enter a weight greater than zero, or leave it blank.`)
        return
      }
      weights.push({ unit_code, kg_per_unit: kg })
    }
    save.mutate({
      categoryId: form.categoryId,
      categoryName: form.categoryName,
      weights,
    })
  }

  return (
    <div className="unit-weights-page">
      <PageHeader
        title="Unit weights"
        subtitle="Kilograms in one bag, roll or bundle — set for each category"
        actions={
          <>
            <Link to="/products/units" className="btn btn-sm">
              Units
            </Link>
            <Link to="/products" className="btn btn-sm">
              Back to products
            </Link>
          </>
        }
      />

      {/* One slot, always the height of an alert, so saving a row does not
          shunt the table down and back as the message comes and goes. */}
      {!form && error ? (
        <Alert tone="danger" message={error} onClose={() => setError('')} />
      ) : (
        <Alert tone="success" message={notice} onClose={() => setNotice('')} reserve />
      )}

      <Drawer
        open={Boolean(form)}
        error={error}
        onErrorDismiss={() => setError('')}
        size="wide"
        title="Unit weights"
        subtitle={form?.categoryName}
        onClose={() => setForm(null)}
        footer={
          <>
            <button type="button" className="btn" onClick={() => setForm(null)}>
              Cancel
            </button>
            <button
              type="submit"
              form="unit-weight-form"
              className="btn btn-primary"
              disabled={save.isPending}
            >
              {save.isPending ? 'Saving…' : 'Save'}
            </button>
          </>
        }
      >
        {form && (
          <form id="unit-weight-form" onSubmit={commit}>
            <div className="form-grid">
              {form.cells.map((cell) => (
                <label className="field" key={cell.unit_code}>
                  <span>{cell.label}</span>
                  <input
                    type="text"
                    inputMode="decimal"
                    autoComplete="off"
                    placeholder="kg"
                    value={form.values[cell.unit_code] ?? ''}
                    onChange={(e) => {
                      // Reject the keystroke rather than the saved value, so
                      // a stray letter never lands in the box at all.
                      if (!DIGITS.test(e.target.value)) return
                      setForm({
                        ...form,
                        values: {
                          ...form.values,
                          [cell.unit_code]: e.target.value,
                        },
                      })
                    }}
                  />
                </label>
              ))}
            </div>
            <p className="hint">
              Kilograms in one of each unit, for every SKU in this category.
              Clearing a box removes that weight again.
            </p>
          </form>
        )}
      </Drawer>

      {!data.length && <Empty icon="⚖️" title="No categories yet" />}

      {data.length > 0 && (
        <table className="table unit-weight-grid">
          <thead>
            <tr>
              <th>Category</th>
              {units.map((u) => (
                <th key={u.unit_code} className="right">
                  {u.label}
                </th>
              ))}
              {editable && <th />}
            </tr>
          </thead>
          <tbody>
            {data.map((row) => (
              <tr key={row.category_id}>
                <td className="strong">
                  {row.category_name}
                  <div className="muted small">{row.product_count} SKUs</div>
                </td>

                {row.units.map((cell) => (
                  <td
                    key={cell.unit_code}
                    className={`right weight-cell weight-${cell.source}`}
                    title={
                      cell.source === 'none'
                        ? 'No weight set for this category'
                        : cell.note || 'Set for this category'
                    }
                  >
                    {cell.kg_per_unit == null ? '—' : `${Number(cell.kg_per_unit)} kg`}
                  </td>
                ))}

                {editable && (
                  <td className="right nowrap">
                    <div className="row-actions">
                      <button
                        type="button"
                        className="btn btn-sm"
                        onClick={() => startEditing(row)}
                      >
                        Edit
                      </button>
                    </div>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      )}

      <p className="hint">
        A dash means this category has not said what the unit weighs —
        {missing === 1 ? ' 1 is' : ` ${missing} are`} unset. A SKU carrying its
        own conversion rate keeps it: that is more specific than a category, so
        it is used first.
      </p>
    </div>
  )
}
